import { EventDrivenStore } from '@core/store/eventDrivenStore';
import constants from '@core/utils/constants';
import { User } from './user';
import { bus } from '@core/events/eventSystem';

class UserStore extends EventDrivenStore {
  constructor() {
    const initialState = {
      current: null,
      loading: false,
      isLoggedIn: false,
      error: null,
      otpData: null,
      token: null
    };
    super('user', initialState, {
      useLocalStorage: true
    });

    // Fix: Ensure isLoggedIn is synced if current user was persisted
    // Also hydrate the user instance
    if (this.state.current) {
      this.state.current = new User(this.state.current);
      this.state.isLoggedIn = true;
    }
  }

  async setupEvents() {
    this.on(constants.EVENTS.USER.LOGIN, () => {
      this.state.loading = true;
      this.state.error = null;
      // Clear stale current user data immediately when login starts,
      // so the old avatar/profile never flashes while awaiting the response
      this.state.current = null;
      this.state.isLoggedIn = false;
    });

    this.on(constants.EVENTS.USER.LOGIN_SUCCESS, async (data) => {
      // Support both direct user object and { user, token } format
      const user = data?.user || data;
      const token = data?.token || null;

      this.state.current = new User(user);
      console.log("🚀 ~ UserStore ~ LOGIN_SUCCESS ~ user:", this.state.current)
      this.state.isLoggedIn = !!this.state.current;
      this.state.userId = user?.id || null;  // Stocker le userId au lieu du token
      this.state.token = null;  // La nouvelle API ne fournit pas de token
      this.state.error = null;
      this.state.loading = false;

      // Clear guest temp ID so the real user ID takes priority
      localStorage.removeItem('user_id');

      // Ensure token is saved for API requests
      if (token) {
        this.saveToken(token);
      }
    });

    this.on(constants.EVENTS.USER.LOGIN_ERROR, async (error) => {
      this.state.error = error?.message || error;
      this.state.loading = false;
      this.state.isLoggedIn = false;
    });

    this.on(constants.EVENTS.USER.REGISTER, async () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.USER.REGISTER_SUCCESS, async (data) => {
      // Support both direct user object and { user, token } format
      const user = data?.user || data;
      const token = data?.token || null;

      this.state.current = new User(user);
      console.log("🚀 ~ UserStore ~ REGISTER_SUCCESS ~ user:", this.state.current)
      this.state.isLoggedIn = !!this.state.current;
      this.state.userId = user?.id || null;  // Stocker le userId au lieu du token
      this.state.token = null;  // La nouvelle API ne fournit pas de token
      this.state.error = null;
      this.state.loading = false;

      // Clear guest temp ID so the real user ID takes priority
      localStorage.removeItem('user_id');

      // Ensure token is saved for API requests
      if (token) {
        this.saveToken(token);
      }

      // Trigger birthday dialog if user doesn't have birthday set
      if (!user?.metadata?.birthday) {
        setTimeout(() => {
          bus.emit('birthday:show');
        }, 1500);
      }
    });

    this.on(constants.EVENTS.USER.REGISTER_ERROR, async (error) => {
      this.state.error = error?.message || error;
      this.state.loading = false;
      this.state.isLoggedIn = false;
    });

    this.on(constants.EVENTS.USER.UPDATE, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.USER.UPDATE_SUCCESS, async (updatedUser) => {
      console.log('🚀 ~ UserStore ~ UPDATE_SUCCESS:', updatedUser);
      const existing = this.state.current;
      this.state.current = new User({
        ...existing,
        ...(updatedUser || {}),
        // Never overwrite profilePictureUrl with undefined — keep existing if updatedUser lacks it
        profilePictureUrl: updatedUser?.profilePictureUrl ?? existing?.profilePictureUrl
      });
      this.state.isLoggedIn = !!this.state.current;
      this.state.error = null;
      this.state.loading = false;
    });

    this.on(constants.EVENTS.USER.REFRESH_DATA_SUCCESS, async (userData) => {
      console.log('🚀 ~ UserStore ~ REFRESH_DATA_SUCCESS:', userData);
      const existing = this.state.current;
      this.state.current = new User({
        ...existing,
        ...(userData || {}),
        // Preserve profilePictureUrl if the refreshed data doesn't include it
        profilePictureUrl: userData?.profilePictureUrl ?? existing?.profilePictureUrl
      });
      this.state.isLoggedIn = !!this.state.current;
      this.state.loading = false;
    });

    this.on(constants.EVENTS.USER.REFRESH_DATA_ERROR, async (error) => {
      this.state.loading = false;
      if (error?.statusCode === 401 || error?.statusCode === 404) {
        bus.emit(constants.EVENTS.USER.LOGOUT);
      }
    });

    this.on(constants.EVENTS.USER.UPDATE_ERROR, async (error) => {
      this.state.error = error?.message || error;
      this.state.loading = false;
    });

    this.on(constants.EVENTS.USER.LOGOUT, async () => {
      this.state.userId = null;  // Effacer aussi le userId
      localStorage.removeItem('user_id'); // Clear guest temp ID
      this.state.current = null;
      this.state.token = null;  // Plus utilisé mais on le garde pour compatibilité
      this.state.isLoggedIn = false;
      bus.emit(constants.EVENTS.SHARED.RELOAD_APP_TO_HOME);
    });

    this.on(constants.EVENTS.USER.FAVORITE_SUCCESS, ({ type, id, isFavorite }) => {
      if (!this.state.current) return;

      let { favoriteRestaurants, favoriteDishes } = this.state.current;
      favoriteRestaurants = [...(favoriteRestaurants || [])];
      favoriteDishes = [...(favoriteDishes || [])];

      if (type === 'restaurant') {
        if (isFavorite && !favoriteRestaurants.includes(id)) {
          favoriteRestaurants.push(id);
        } else if (!isFavorite) {
          favoriteRestaurants = favoriteRestaurants.filter(rid => rid !== id);
        }
      } else if (type === 'dish') {
        if (isFavorite && !favoriteDishes.includes(id)) {
          favoriteDishes.push(id);
        } else if (!isFavorite) {
          favoriteDishes = favoriteDishes.filter(did => did !== id);
        }
      }

      this.state.current = new User({
        ...this.state.current,
        favoriteRestaurants,
        favoriteDishes
      });
    });

    // When full favorites are loaded, sync the IDs into the user model
    // so that DishCard / RestaurantCard isFavorite computed is always up-to-date
    this.on(constants.EVENTS.USER.FAVORITES_LOAD_SUCCESS, ({ dishes, restaurants }) => {
      if (!this.state.current) return;

      const favoriteDishes = (dishes || []).map(d => d.id).filter(Boolean);
      const favoriteRestaurants = (restaurants || []).map(r => r.id).filter(Boolean);

      this.state.current = new User({
        ...this.state.current,
        favoriteDishes,
        favoriteRestaurants
      });
    });

    // Trigger favorites load as soon as the app is ready and user is logged in
    bus.on('app.ready', () => {
      if (this.state.current?.id) {
        bus.emit(constants.EVENTS.USER.FAVORITES_LOAD);
      }
    });

    // Also load favorites when user logs in or registers
    this.on(constants.EVENTS.USER.LOGIN_SUCCESS, () => {
      setTimeout(() => bus.emit(constants.EVENTS.USER.FAVORITES_LOAD), 500);
    });
    this.on(constants.EVENTS.USER.REGISTER_SUCCESS, () => {
      setTimeout(() => bus.emit(constants.EVENTS.USER.FAVORITES_LOAD), 500);
    });
  }

  saveToken(token) {
    try {
      localStorage.setItem('auth_token', token);
    } catch (error) {
      console.warn('[UserStore] Failed to save token:', error);
    }
  }

  clearToken() {
    try {
      localStorage.removeItem('auth_token');
    } catch (error) {
      console.warn('[UserStore] Failed to clear token:', error);
    }
  }

  getToken() {
    return this.state.token || localStorage.getItem('auth_token');
  }
}

export default UserStore;
